import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { startTransition, useCallback, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Icon } from '@column/column-ui-kit';

import { PopoverPlatform, PopoverPlatformType } from '~/components';
import { UserModel } from '~/models';
import { UserRepository } from '~/repositories';
import { getRoleLabel } from '~/repositories/RoleRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { log } from '~/util';
import { reCaptchaCheck } from '~/util/reCaptcha';

const Wrapper = styled.div`
  padding: 0 12px;
  position: relative;
`;

const Action = styled.div<{ $isSelectable: boolean }>`
  position: relative;
  display: flex;
  gap: 0;
  width: 100%;
  align-items: center;
  padding: 6px 10px 6px 12px;
  box-sizing: border-box;
  line-height: 24px;
  margin-top: -1px;

  &::after {
    content: '';
    position: absolute;
    inset: 1px;
    background-color: ${({ theme }) => theme.secondary.blendToBackground(35)};
    box-shadow:
      0 -1px 1px
        color-mix(
          in srgb,
          ${({ theme }) => (theme.id === 'dark' ? `${theme.secondary.blendToBackground(300)}` : `${theme.background}`)}
            75%,
          transparent
        )
        inset,
      ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
        ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
        ${({ theme }) => theme.style.buttonSecondaryBorderColor},
      0 1px 1px rgba(0, 0, 0, 0.075),
      0 1px 4px 0 transparent inset;
    border-radius: 8px;
    z-index: -1;
    transition:
      box-shadow 0.2s ease-in-out,
      background 0.2s ease-in-out;
  }

  ${({ $isSelectable }) =>
    !$isSelectable &&
    css`
      cursor: default;
    `}

  ${({ $isSelectable }) =>
    $isSelectable &&
    css`
      cursor: pointer;

      &:hover {
        &::after {
          background-color: ${({ theme }) => theme.style.buttonSecondaryHoveredBackgroundColor};
          box-shadow:
            0 -1px 1px rgba(255, 255, 255, 0.75) inset,
            ${({ theme }) => theme.style.buttonSecondaryHoveredBorderProperties}
              ${({ theme }) => theme.style.buttonSecondaryHoveredBorderWidth}
              ${({ theme }) => theme.secondary.blendToBackground(1100, 150)},
            0 1px 1px rgba(0, 0, 0, 0),
            0 1px 4px 0 ${({ theme }) => theme.secondary.blendToBackground(120)} inset;

          svg {
            --icon-color: ${({ theme }) => theme.secondary.background};
          }
        }
      }
    `}
`;

const ActionLabel = styled.div`
  display: flex;
  flex: auto;
  align-items: center;
  gap: 8px;
  line-height: 24px;
  font-weight: 500;
  color: ${({ theme }) => theme.style.buttonSecondaryColor};
  transition: color 0.2s;
  overflow: hidden;
  white-space: nowrap;
  padding-right: 4px;

  > svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  }
`;

const spinAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const PlatformLoadingIconWrapper = styled.div<{ $isVisible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  will-change: opacity, transform, width, margin-right;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transform: scale(${({ $isVisible }) => ($isVisible ? 1 : 0.3)});
  width: ${({ $isVisible }) => ($isVisible ? '16px' : '0')};
  margin-right: ${({ $isVisible }) => ($isVisible ? '6px' : '0')};
  transition-property: opacity, transform, width, margin-right;
  transition-duration: ${({ $isVisible }) => ($isVisible ? '0s' : '0.3s')};
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: 0s;
`;

const PlatformLoadingIcon = styled(Icon.Loading)`
  --icon-size: 16px;
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  flex-shrink: 0;
  animation: ${spinAnimation} 1s linear infinite;
`;

const PlatformNameText = styled.span<{ $isLoading?: boolean }>`
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  will-change: opacity;
  opacity: ${({ $isLoading }) => ($isLoading ? 0.5 : 1)};
  transition-property: opacity;
  transition-duration: 0.3s;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: 0s;
`;

const PlatformName = styled.div<{ size?: 'small' | 'large'; 'data-loading'?: boolean }>`
  height: 24px;
  font-size: 14px;
  line-height: 23px;
  vertical-align: middle;
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;

  ${({ size }) =>
    size === 'small' &&
    css`
      color: ${({ theme }) => theme.secondary.blendToBackground(500)};
      display: flex;
      align-items: center;
      gap: 8px;
    `}
`;

const Invites = styled.div`
  width: 16px;
  height: 16px;
  line-height: 16px;
  min-width: 16px;
  text-align: center;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  color: ${({ theme }) => theme.primary.foreground};
  background-color: ${({ theme }) => theme.primary.background};
`;

const Arrow = styled.div`
  & > svg {
    --icon-size: 20px;
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
    cursor: pointer;

    path {
      transition:
        stroke 0.2s,
        fill 0.2s;
    }
  }
`;

const StyledPopoverPlatform = styled(PopoverPlatform)`
  --popover-left: 13px;
  --popover-top: 95px;
  --popover-min-width: 222px;
`;

interface PlatformProps {
  onNavigate?: () => void;
}

export const Platform: React.FC<PlatformProps> = ({ onNavigate }) => {
  const {
    currentUser,
    currentPlatform,
    platformRole,
    setCurrentUser,
    refetchCurrentUser,
    setPlatform,
    loadPlatform,
    isSandbox,
  } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const [showPopoverPlatform, setShowPopoverPlatform] = useState<boolean>(false);
  const [isLoadingPlatform, setIsLoadingPlatform] = useState<boolean>(false);
  const [pendingPlatformName, setPendingPlatformName] = useState<string | undefined>(undefined);
  const actionRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data: invitesData } = useQuery({
    queryKey: ['platformInvites', currentUser?.id],
    queryFn: () => UserRepository.getInvites({ dashboardUserId: currentUser!.id }),
    enabled: !!currentUser?.id,
    initialData: () =>
      currentUser?.platformInvites
        ? {
            invites: currentUser.platformInvites.map((entry) => ({ ...entry })),
          }
        : undefined,
  });

  const invites = useMemo<PopoverPlatformType[]>(() => {
    if (!invitesData?.invites || invitesData.invites.length === 0) {
      return [];
    }

    return invitesData.invites.map((entry) => ({
      id: entry.platformId,
      label: entry.platformName,
      role: getRoleLabel(entry.platformRole),
      isInvite: true,
    }));
  }, [invitesData?.invites]);

  const platforms = useMemo<PopoverPlatformType[]>(() => {
    if (!currentUser?.platformRoles || currentUser.platformRoles.length === 0) {
      return [];
    }

    return currentUser.platformRoles.map((entry) => ({
      id: entry.platformId,
      label: entry.platformName,
      role: getRoleLabel(entry.name),
    }));
  }, [currentUser?.platformRoles]);

  const { handleReCaptcha } = reCaptchaCheck();

  const acceptInviteMutation = useMutation({
    mutationFn: ({ platformId, inviteCode }: { platformId: string; inviteCode?: string }) =>
      UserRepository.acceptInvite({ platformId, inviteCode }),
    onError: (error: unknown) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Something went wrong, please try again.',
      });
    },
    onSuccess: async () => {
      addSuccessNotification({
        content: 'Invite accepted',
      });

      refetchCurrentUser();
      queryClient.invalidateQueries({ queryKey: ['platformInvites', currentUser?.id] });

      setShowPopoverPlatform(false);
      onNavigate?.();
    },
  });

  const acceptInvite = (id: string) => {
    if (acceptInviteMutation.isPending) {
      return;
    }

    acceptInviteMutation.mutate({ platformId: id });
  };

  const activeChange = (id: string) => {
    if (!currentUser) {
      return;
    }

    const updatedUser = new UserModel({
      ...currentUser,
      defaultPlatformId: id,
    });

    const newPlatformRole = currentUser.platformRoles.find((role) => role.platformId === id);

    if (!newPlatformRole) {
      addDangerNotification({
        content: 'Platform role not found',
      });
      return;
    }

    setIsLoadingPlatform(true);
    setPendingPlatformName(newPlatformRole.platformName);
    setShowPopoverPlatform(false);

    if (!newPlatformRole.isLiveEnabled && !isSandbox) {
      useSessionStore.setState({ isSandbox: true });
    }

    startTransition(() => {
      setCurrentUser(updatedUser);
      setPlatform(undefined, newPlatformRole);
    });

    requestAnimationFrame(() => {
      loadPlatform({ user: updatedUser })
        .then(() => {
          setIsLoadingPlatform(false);
          setPendingPlatformName(undefined);
        })
        .catch(() => {
          setIsLoadingPlatform(false);
          setPendingPlatformName(undefined);
        });
    });

    handleReCaptcha(
      (reCaptchaToken) =>
        currentUser
          .update({ defaultPlatformId: id }, reCaptchaToken)
          .then(() => {
            log({
              name: 'Platform changed',
              context: updatedUser,
            });
            addSuccessNotification({
              content: 'Platform changed',
            });
          })
          .catch((error) => {
            setCurrentUser(currentUser);
            setPlatform(undefined, platformRole);
            setIsLoadingPlatform(false);
            setPendingPlatformName(undefined);
            addDangerNotification({
              content: error.message,
            });
          }),
      (err) => {
        setCurrentUser(currentUser);
        setPlatform(undefined, platformRole);
        setIsLoadingPlatform(false);
        setPendingPlatformName(undefined);
        addDangerNotification({
          content: err.message,
        });
      }
    );
  };

  const handleTriggerClick = useCallback(() => {
    setShowPopoverPlatform((s) => !s);
  }, []);

  const handlePopoverClose = useCallback(
    (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }
      if (actionRef.current && (event.target === actionRef.current || actionRef.current.contains(event.target))) {
        return;
      }
      setShowPopoverPlatform(false);
    },
    [actionRef]
  );

  const displayPlatformName = pendingPlatformName || currentPlatform?.name;
  const hasPlatforms = invites.length > 0 || platforms.length > 0;
  return (
    <Wrapper>
      <Action onClick={handleTriggerClick} ref={actionRef} $isSelectable={hasPlatforms}>
        <ActionLabel>
          {currentPlatform?.name ? (
            <PlatformName size={'large'} data-loading={isLoadingPlatform} key="platform-selected">
              <PlatformLoadingIconWrapper $isVisible={isLoadingPlatform}>
                <PlatformLoadingIcon />
              </PlatformLoadingIconWrapper>
              <PlatformNameText $isLoading={isLoadingPlatform}>{displayPlatformName}</PlatformNameText>
            </PlatformName>
          ) : pendingPlatformName ? (
            <PlatformName size={'small'} data-loading={false} key="loading-platform">
              <PlatformLoadingIcon />
              Loading platform
            </PlatformName>
          ) : (
            <PlatformName size={'small'} data-loading={false} key="no-platform">
              No platform selected
            </PlatformName>
          )}
          {invites.length > 0 && <Invites>{invites.length}</Invites>}
        </ActionLabel>

        {hasPlatforms && <Arrow>{showPopoverPlatform ? <Icon.ChevronUp /> : <Icon.ChevronDown />}</Arrow>}
      </Action>

      <StyledPopoverPlatform
        platforms={[...invites, ...platforms]}
        active={currentUser?.defaultPlatformId}
        show={showPopoverPlatform}
        usePortal
        anchorRef={actionRef}
        placement="bottom-start"
        offset={8}
        onActiveChange={activeChange}
        onAcceptClick={acceptInvite}
        onClose={handlePopoverClose}
      />
    </Wrapper>
  );
};
